import { useState } from 'react';

interface RenameVariableDialogProps {
  oldName: string;
  defaultNewName?: string;
  onClose: () => void;
  onRename: (newName: string) => Promise<{ affected_files: string[]; summary: string }>;
}

const NAME_RE = /^[A-Z][A-Z0-9_]*$/;

export function RenameVariableDialog({
  oldName,
  defaultNewName,
  onClose,
  onRename,
}: RenameVariableDialogProps) {
  const [newName, setNewName] = useState(defaultNewName ?? oldName);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<{ affected_files: string[]; summary: string } | null>(
    null,
  );

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!NAME_RE.test(newName)) {
      setError('New name must be UPPER_SNAKE_CASE (letters, digits, underscores; starts with a letter).');
      return;
    }
    if (newName === oldName) {
      setError('New name is the same as the old name.');
      return;
    }
    setBusy(true);
    try {
      const result = await onRename(newName);
      setPreview(result);
      setBusy(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Rename failed');
      setBusy(false);
    }
  };

  if (preview) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
        <div
          className="w-full max-w-md rounded-lg border bg-canvas p-5 text-sm"
          style={{ boxShadow: '0 20px 60px rgba(0,0,0,.18)' }}
        >
          <div className="flex items-center gap-2">
            <span
              className="h-[10px] w-[10px] rounded-full"
              style={{ background: 'var(--color-success)' }}
            />
            <h2 className="text-base font-semibold">Variable renamed</h2>
          </div>
          <p className="mt-2 text-[13px]">{preview.summary}</p>
          <div className="mt-3 rounded border bg-surface p-3">
            <p className="text-[10px] uppercase tracking-wider text-text-subtle">
              Blast radius — {preview.affected_files.length} file
              {preview.affected_files.length === 1 ? '' : 's'} changed
            </p>
            <ul className="mt-2 space-y-1 font-mono text-[12px]">
              {preview.affected_files.map((p) => (
                <li key={p} className="flex items-center gap-2">
                  <span
                    className="h-[6px] w-[6px] rounded-full"
                    style={{ background: 'var(--color-success)' }}
                  />
                  {p}
                </li>
              ))}
            </ul>
          </div>
          <div className="mt-4 flex justify-end">
            <button type="button" onClick={onClose} className="btn btn-primary">
              Done
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <form
        onSubmit={submit}
        className="w-full max-w-md rounded-lg border bg-canvas p-5 text-sm"
        style={{ boxShadow: '0 20px 60px rgba(0,0,0,.18)' }}
      >
        <h2 className="text-base font-semibold">Rename variable</h2>
        <p className="mt-1 text-[12px] text-text-subtle">
          Renames <code className="font-mono">${'{'}{oldName}{'}'}</code> across SKILL.md and every
          reference file in one atomic save. Use UPPER_SNAKE_CASE.
        </p>

        <label className="mt-4 block text-[10px] uppercase tracking-wider text-text-subtle">
          From
          <div className="mt-1 rounded border bg-surface px-2 py-1 font-mono text-[13px] text-text-muted">
            ${'{'}{oldName}{'}'}
          </div>
        </label>

        <label className="mt-3 block text-[10px] uppercase tracking-wider text-text-subtle">
          To
          <div className="mt-1 flex items-center rounded border bg-surface px-2 py-1 font-mono text-[13px]">
            <span className="text-text-subtle">${'{'}</span>
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value.toUpperCase())}
              autoFocus
              className="flex-1 bg-transparent outline-none"
            />
            <span className="text-text-subtle">{'}'}</span>
          </div>
        </label>

        {error && (
          <p className="mt-3 rounded border bg-danger-soft p-2 text-[11px] text-[var(--color-danger)]">
            {error}
          </p>
        )}

        <div className="mt-4 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="btn">
            Cancel
          </button>
          <button type="submit" disabled={busy} className="btn btn-primary">
            {busy ? 'Renaming…' : 'Rename atomically'}
          </button>
        </div>
      </form>
    </div>
  );
}
